import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { RevealLines, FadeUp, Overline } from "./Reveal";
import { requestService } from "./scrollUtils";

const INGRESSO = [
  {
    name: "Armocromia",
    price: "€150",
    badge: "Metodo a 12 stagioni",
    desc: "Intro colorimetria, analisi del sottotono, test comparativo dei drappi, assegnazione stagione e sottogruppo, palette digitale colori, report digitale capelli e make-up.",
  },
  {
    name: "Body Shape",
    price: "€180",
    desc: "Analisi proporzioni, studio percezione, consigli capi ed accessori valorizzanti, styling pratico su 5 capi del guardaroba con scatti fotografici live, report digitale riassuntivo.",
  },
];

const SINGOLI = [
  {
    name: "Restyling\nGuardaroba",
    price: "€250",
    duration: "2 ore",
    desc: "Nuova vita al tuo guardaroba stagionale con styling personalizzato. Strutturazione capsule selection, prove outfit con scatti fotografici live e shopping list strategica.",
  },
  {
    name: "Riorganizzazione Armadio",
    price: "€350",
    duration: "4 ore",
    desc: "Less is more. Detox dei capi usurati o non valorizzanti, riorganizzazione completa per categoria / scala cromatica / occasione d'uso e scatti fotografici finali.",
  },
  {
    name: "Accompagnamento Shopping",
    price: "€270",
    duration: "2 ore",
    desc: "Studio di stile, ricerca preliminare sui capi e sessione d'acquisto guidata live. Look su misura per la stagione o per occasioni speciali, sempre nel rispetto del tuo budget.",
  },
];

const Card = ({ item, index, highlight }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-8%" }}
    transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    className={`group border border-espresso/10 p-8 sm:p-9 flex flex-col hover:border-fuchsiabrand transition-colors duration-500 ${
      highlight
        ? "bg-white shadow-[0_18px_50px_-30px_rgba(42,42,42,0.35)]"
        : "bg-white/90 backdrop-blur-sm shadow-[0_12px_40px_-30px_rgba(42,42,42,0.3)]"
    }`}
    data-testid={`servizio-${item.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z-]/g, "")}`}
  >
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h3 className="font-display text-xl sm:text-2xl text-espresso leading-tight whitespace-pre-line">
          {item.name}
        </h3>
        {item.duration && (
          <span className="mt-1 inline-block font-mono text-[10px] uppercase tracking-[0.18em] text-stone/70">
            Durata · {item.duration}
          </span>
        )}
      </div>
      <span className="font-display text-2xl text-fuchsiabrand flex-shrink-0">{item.price}</span>
    </div>

    {item.badge && (
      <span className="inline-block self-start mb-4 bg-fuchsiabrand text-white font-mono text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 rounded-full">
        {item.badge}
      </span>
    )}

    <p className="text-stone leading-relaxed text-[15px] flex-1">{item.desc}</p>

    <button
      onClick={() => requestService(item.name)}
      data-testid={`servizio-cta-${item.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z-]/g, "")}`}
      className="mt-7 group/btn w-full inline-flex items-center justify-center gap-2 bg-espresso text-crema py-3.5 px-6 rounded-full font-mono text-xs uppercase tracking-[0.15em] hover:bg-fuchsiabrand transition-colors duration-300"
    >
      Prenota questo servizio
      <ArrowUpRight size={16} className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform duration-300" />
    </button>
  </motion.div>
);

export const Servizi = () => {
  return (
    <section id="servizi" className="relative py-24 sm:py-36 overflow-hidden" data-testid="servizi-section">
      {/* Capsule wardrobe mood — transparent backdrop, bottom faded to hide the floor */}
      <div
        aria-hidden="true"
        data-testid="servizi-bg-photo"
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.32] bg-cover bg-top"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/brand/servizi-capsule.jpg)`,
          maskImage: "linear-gradient(to bottom, transparent 0%, black 10%, black 48%, transparent 82%)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 10%, black 48%, transparent 82%)",
        }}
      />
      {/* Soft light scrim to keep cards and text fully legible */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 bg-crema/45" />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 relative z-10">
        <Overline className="mb-8">Servizi Singoli & d'Ingresso</Overline>
        <h2 className="font-display uppercase text-fuchsiabrand text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl">
          <RevealLines
            lines={[
              <>I <span className="text-espresso">fondamentali</span></>,
              "per iniziare",
            ]}
          />
        </h2>

        {/* Ingresso */}
        <FadeUp className="mt-16 mb-6">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-espresso">
            Servizi d'Ingresso — I Fondamentali
          </span>
        </FadeUp>
        <div className="grid md:grid-cols-2 gap-6 items-stretch">
          {INGRESSO.map((it, i) => (
            <Card key={it.name} item={it} index={i} highlight />
          ))}
        </div>

        {/* Singoli */}
        <FadeUp className="mt-16 mb-6">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-espresso">
            Servizi Singoli su Richiesta
          </span>
        </FadeUp>
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {SINGOLI.map((it, i) => (
            <Card key={it.name} item={it} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
